/*
 * This file is part of the Det Norske Teatret application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect } from 'react';

interface ResponsiveTitleProps {
  min?: number;
  max?: number;
  step?: number;
  offset?: number;
  children: string;
}

/**
 * This is the ResponsiveTitle component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function ResponsiveTitle({
  min = 46,
  max = 176,
  step = 10,
  offset = 20,
  children,
}: ResponsiveTitleProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const [size, setSize] = useState<number | null>(null);
  const limit = max - min;

  useEffect(() => {
    function onResize() {
      setSize(null);
    }

    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [size]);

  useEffect(() => {
    if (ref.current!.offsetWidth + offset > window.innerWidth) {
      setSize(prev => {
        if (prev === null) {
          return 0;
        }

        if (prev < limit) {
          return prev + step;
        }

        return prev;
      });
    } else if (size === null) {
      setSize(0);
    }
  }, [size, step, limit]);

  function getStyle() {
    if (size === null) return undefined;

    return {
      fontSize: `${(max - size) / 16}em`,
    };
  }

  return (
    <h1 style={getStyle()}>
      <span ref={ref} dangerouslySetInnerHTML={{ __html: children }} />
    </h1>
  );
}

export default ResponsiveTitle;
